import React, { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Flex,
  Text,
  Avatar,
  Button,
  Input,
  Stack,
  Heading,
  FormControl,
  FormLabel,
  FormErrorMessage,
  VStack,
  HStack,
  IconButton,
  Badge,
  Image,
  Radio,
  RadioGroup,
  Spinner,
  useDisclosure,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import {
  EditIcon,
  DeleteIcon,
  ArrowBackIcon,
  AttachmentIcon,
} from "@chakra-ui/icons";
import { useUser } from "../context/UserContext";
import { useNavigate } from "react-router-dom";
import {
  deleteProfileImage,
  deleteDigitalSignature,
  saveProfileImageUrl,
  saveDigitalSignatureUrl,
  uploadProfileImage,
  uploadDigitalSignature,
} from "../services/userService";
import {
  isSignatureFileSizeValid,
  isSupportedSignatureFile,
  isValidSignatureUrl,
} from "../utils/digitalSignature";

const formatSignatureError = (error) =>
  error?.response?.data?.message || error?.message || "Signature update failed";

const formatProfileImageError = (error) =>
  error?.response?.data?.message || error?.message || "Profile image update failed";

const PROFILE_IMAGE_ALLOWED_MIME_TYPES = ["image/png", "image/jpeg", "image/webp"];
const PROFILE_IMAGE_MAX_SIZE = 5 * 1024 * 1024;

const isSupportedProfileImageFile = (file) =>
  Boolean(file && PROFILE_IMAGE_ALLOWED_MIME_TYPES.includes(file.type));

const isProfileImageFileSizeValid = (file) =>
  Boolean(file && file.size <= PROFILE_IMAGE_MAX_SIZE);

const MyProfile = () => {
  // Dark mode colors
  const bgColor = useColorModeValue("#f9f9f9", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("#e2e8f0", "gray.600");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const selectedBg = useColorModeValue("orange.50", "orange.900");
  const selectedColor = useColorModeValue("orange.500", "orange.300");
  const previewBg = useColorModeValue("gray.50", "gray.700");

  const { loggedInUser, setLoggedInUser } = useUser();
  const [editMode, setEditMode] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState("My Profile");
  const currentUserId = loggedInUser?.id || loggedInUser?._id;
  const [profileImageMode, setProfileImageMode] = useState(
    loggedInUser?.profileImage?.type || "upload",
  );
  const [profileImageUrl, setProfileImageUrl] = useState(
    loggedInUser?.profileImage?.type === "url" ? loggedInUser.profileImage.url : "",
  );
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(
    loggedInUser?.profileImage?.url || "",
  );
  const [profileImageError, setProfileImageError] = useState("");
  const [profileImagePreviewError, setProfileImagePreviewError] = useState(false);
  const [isSavingProfileImage, setIsSavingProfileImage] = useState(false);
  const [isDeletingProfileImage, setIsDeletingProfileImage] = useState(false);
  const [signatureMode, setSignatureMode] = useState(
    loggedInUser?.digitalSignature?.type || "upload",
  );
  const [signatureUrl, setSignatureUrl] = useState(
    loggedInUser?.digitalSignature?.type === "url"
      ? loggedInUser.digitalSignature.url
      : "",
  );
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(
    loggedInUser?.digitalSignature?.url || "",
  );
  const [signatureError, setSignatureError] = useState("");
  const [previewError, setPreviewError] = useState(false);
  const [isSavingSignature, setIsSavingSignature] = useState(false);
  const [isDeletingSignature, setIsDeletingSignature] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();
  const toast = useToast();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: loggedInUser?.name || "",
    email: loggedInUser?.email || "",
    phone: loggedInUser?.phone || "",
    password: "",
    role: loggedInUser?.role || "Employee",
    country: loggedInUser?.country || "India",
  });

  useEffect(() => {
    setForm({
      name: loggedInUser?.name || "",
      email: loggedInUser?.email || "",
      phone: loggedInUser?.phone || "",
      password: "",
      role: loggedInUser?.role || "Employee",
      country: loggedInUser?.country || "India",
    });
    setProfileImageMode(loggedInUser?.profileImage?.type || "upload");
    setProfileImageUrl(
      loggedInUser?.profileImage?.type === "url"
        ? loggedInUser.profileImage.url
        : "",
    );
    setProfileImageFile(null);
    setProfileImagePreview(loggedInUser?.profileImage?.url || "");
    setProfileImageError("");
    setProfileImagePreviewError(false);
    setSignatureMode(loggedInUser?.digitalSignature?.type || "upload");
    setSignatureUrl(
      loggedInUser?.digitalSignature?.type === "url"
        ? loggedInUser.digitalSignature.url
        : "",
    );
    setSignatureFile(null);
    setSignaturePreview(loggedInUser?.digitalSignature?.url || "");
    setSignatureError("");
    setPreviewError(false);
  }, [loggedInUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    const updatedUser = {
      ...loggedInUser,
      name: form.name,
      email: form.email,
      phone: form.phone,
    };
    setLoggedInUser(updatedUser);
    localStorage.setItem("user", JSON.stringify(updatedUser));
    setEditMode(false);
  };

  const updateCachedSignature = (digitalSignature) => {
    const updatedUser = {
      ...loggedInUser,
      digitalSignature,
    };
    setLoggedInUser(updatedUser);
    localStorage.setItem("user", JSON.stringify(updatedUser));
    setSignaturePreview(digitalSignature?.url || "");
    setSignatureFile(null);
    setPreviewError(false);
  };

  const updateCachedProfileImage = (profileImage) => {
    const updatedUser = {
      ...loggedInUser,
      profileImage,
    };
    setLoggedInUser(updatedUser);
    localStorage.setItem("user", JSON.stringify(updatedUser));
    setProfileImagePreview(profileImage?.url || "");
    setProfileImageFile(null);
    setProfileImagePreviewError(false);
  };

  const handleProfileImageFileChange = (event) => {
    const file = event.target.files?.[0];
    setProfileImageError("");
    setProfileImagePreviewError(false);

    if (!file) {
      setProfileImageFile(null);
      setProfileImagePreview(loggedInUser?.profileImage?.url || "");
      return;
    }

    if (!isSupportedProfileImageFile(file)) {
      setProfileImageError("Use a PNG, JPEG, or WebP image.");
      setProfileImageFile(null);
      return;
    }

    if (!isProfileImageFileSizeValid(file)) {
      setProfileImageError("Profile image must be 5MB or smaller.");
      setProfileImageFile(null);
      return;
    }

    setProfileImageMode("upload");
    setProfileImageFile(file);
    setProfileImagePreview(URL.createObjectURL(file));
  };

  const handleProfileImageUrlChange = (event) => {
    const value = event.target.value;
    setProfileImageUrl(value);
    setProfileImageError("");
    setProfileImagePreviewError(false);
    setProfileImagePreview(isValidSignatureUrl(value) ? value.trim() : "");
  };

  const handleProfileImageModeChange = (mode) => {
    setProfileImageMode(mode);
    setProfileImageError("");
    setProfileImagePreviewError(false);
    setProfileImagePreview(
      mode === "url" && isValidSignatureUrl(profileImageUrl)
        ? profileImageUrl.trim()
        : loggedInUser?.profileImage?.url || "",
    );
  };

  const handleSaveProfileImage = async () => {
    if (!currentUserId) return;

    setProfileImageError("");
    setIsSavingProfileImage(true);

    try {
      const response =
        profileImageMode === "upload"
          ? await uploadProfileImage(currentUserId, profileImageFile)
          : await saveProfileImageUrl(currentUserId, profileImageUrl);

      updateCachedProfileImage(response.data);
      toast({
        title: "Profile image saved",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (error) {
      const message = formatProfileImageError(error);
      setProfileImageError(message);
      toast({
        title: "Could not save profile image",
        description: message,
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setIsSavingProfileImage(false);
    }
  };

  const handleDeleteProfileImage = async () => {
    if (!currentUserId) return;

    setIsDeletingProfileImage(true);

    try {
      await deleteProfileImage(currentUserId);
      updateCachedProfileImage(null);
      setProfileImageUrl("");
      toast({
        title: "Profile image removed",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Could not remove profile image",
        description: formatProfileImageError(error),
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setIsDeletingProfileImage(false);
    }
  };

  const handleSignatureFileChange = (event) => {
    const file = event.target.files?.[0];
    setSignatureError("");
    setPreviewError(false);

    if (!file) {
      setSignatureFile(null);
      setSignaturePreview(loggedInUser?.digitalSignature?.url || "");
      return;
    }

    if (!isSupportedSignatureFile(file)) {
      setSignatureError("Use a PNG, JPEG, or WebP image.");
      setSignatureFile(null);
      return;
    }

    if (!isSignatureFileSizeValid(file)) {
      setSignatureError("Signature image must be 5MB or smaller.");
      setSignatureFile(null);
      return;
    }

    setSignatureFile(file);
    setSignaturePreview(URL.createObjectURL(file));
  };

  const handleSignatureUrlChange = (event) => {
    const value = event.target.value;
    setSignatureUrl(value);
    setSignatureError("");
    setPreviewError(false);
    setSignaturePreview(isValidSignatureUrl(value) ? value.trim() : "");
  };

  const handleSignatureModeChange = (mode) => {
    setSignatureMode(mode);
    setSignatureError("");
    setPreviewError(false);
    setSignaturePreview(
      mode === "url" && isValidSignatureUrl(signatureUrl)
        ? signatureUrl.trim()
        : loggedInUser?.digitalSignature?.url || "",
    );
  };

  const handleSaveSignature = async () => {
    if (!currentUserId) return;

    setSignatureError("");
    setIsSavingSignature(true);

    try {
      const response =
        signatureMode === "upload"
          ? await uploadDigitalSignature(currentUserId, signatureFile)
          : await saveDigitalSignatureUrl(currentUserId, signatureUrl);

      updateCachedSignature(response.data);
      toast({
        title: "Digital signature saved",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (error) {
      setSignatureError(formatSignatureError(error));
      toast({
        title: "Could not save signature",
        description: formatSignatureError(error),
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setIsSavingSignature(false);
    }
  };

  const handleDeleteSignature = async () => {
    if (!currentUserId) return;

    setIsDeletingSignature(true);

    try {
      await deleteDigitalSignature(currentUserId);
      updateCachedSignature(null);
      setSignatureUrl("");
      onClose();
      toast({
        title: "Digital signature removed",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Could not remove signature",
        description: formatSignatureError(error),
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setIsDeletingSignature(false);
    }
  };

  const hasSignature = Boolean(loggedInUser?.digitalSignature?.url);
  const hasProfileImage = Boolean(loggedInUser?.profileImage?.url);
  const canSaveProfileImage =
    profileImageMode === "upload"
      ? Boolean(profileImageFile)
      : isValidSignatureUrl(profileImageUrl);
  const canSaveSignature =
    signatureMode === "upload"
      ? Boolean(signatureFile)
      : isValidSignatureUrl(signatureUrl);

  const menuItems = [
    "My Profile",
    "Payslips",
    "Leave History",
    "Salary Structure",
    "Settings",
    "Notifications",
  ];

  return (
    <Flex bg={bgColor} minH="100vh" px={1}>
      {/* Sidebar */}
      <Box
        w="250px"
        bg={cardBg}
        borderRight="1px solid"
        borderColor={borderColor}
        pt={6}
        px={4}
        minH="100vh"
      >
        <IconButton
          icon={<ArrowBackIcon />}
          onClick={() => navigate("/")}
          variant="ghost"
          aria-label="Back"
          mb={6}
        />

        <VStack spacing={2} align="stretch">
          {menuItems.map((item) => (
            <Box
              key={item}
              px={4}
              py={2}
              cursor="pointer"
              bg={selectedMenu === item ? selectedBg : "transparent"}
              color={selectedMenu === item ? selectedColor : textColor}
              fontWeight={selectedMenu === item ? "bold" : "normal"}
              borderRadius="md"
              _hover={{
                bg: selectedBg,
                color: selectedColor,
              }}
              onClick={() => setSelectedMenu(item)}
            >
              <Flex justify="space-between" align="center">
                <Text>{item}</Text>
                {item === "Notifications" && (
                  <Badge colorScheme="green" borderRadius="full" fontSize="xs">
                    ●
                  </Badge>
                )}
              </Flex>
            </Box>
          ))}
        </VStack>
      </Box>

      {/* Main Content */}
      <Box flex="1" p={10}>
        <Flex justify="space-between" align="center" mb={6}>
          <Heading size="md">My Profile</Heading>
          {!editMode ? (
            <Button
              leftIcon={<EditIcon />}
              colorScheme="blue"
              onClick={() => setEditMode(true)}
            >
              Edit
            </Button>
          ) : (
            <HStack>
              <Button variant="ghost" onClick={() => setEditMode(false)}>
                Cancel
              </Button>
              <Button colorScheme="orange" onClick={handleSave}>
                Save
              </Button>
            </HStack>
          )}
        </Flex>

        <Text color={mutedColor} fontSize="sm" mb={6}>
          View and update your personal information.
        </Text>

        <Box
          bg={cardBg}
          borderRadius="lg"
          boxShadow="sm"
          p={8}
          maxW="800px"
          mx="auto"
          borderColor={borderColor}
          border="1px solid"
        >
          <Text fontWeight="semibold" mb={2}>
            Profile Photo
          </Text>
          <Flex align="center" gap={4} mb={6}>
            <Avatar
              name={form.name}
              src={
                profileImagePreview && !profileImagePreviewError
                  ? profileImagePreview
                  : undefined
              }
              size="xl"
              onError={() => setProfileImagePreviewError(true)}
            />
            <Stack spacing={3}>
              <RadioGroup
                value={profileImageMode}
                onChange={handleProfileImageModeChange}
              >
                <HStack spacing={5}>
                  <Radio value="upload">Upload Image</Radio>
                  <Radio value="url">Image URL</Radio>
                </HStack>
              </RadioGroup>
              {profileImageMode === "upload" ? (
                <HStack>
                  <Button
                    as="label"
                    size="sm"
                    colorScheme="gray"
                    cursor="pointer"
                  >
                    Change
                    <Input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      display="none"
                      onChange={handleProfileImageFileChange}
                    />
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="orange"
                    onClick={handleSaveProfileImage}
                    isDisabled={!canSaveProfileImage || isSavingProfileImage}
                    isLoading={isSavingProfileImage}
                  >
                    Save
                  </Button>
                </HStack>
              ) : (
                <HStack align="flex-start">
                  <FormControl isInvalid={Boolean(profileImageError)}>
                    <Input
                      size="sm"
                      value={profileImageUrl}
                      onChange={handleProfileImageUrlChange}
                      placeholder="https://example.com/profile.png"
                    />
                    <FormErrorMessage>{profileImageError}</FormErrorMessage>
                  </FormControl>
                  <Button
                    size="sm"
                    colorScheme="orange"
                    onClick={handleSaveProfileImage}
                    isDisabled={!canSaveProfileImage || isSavingProfileImage}
                    isLoading={isSavingProfileImage}
                  >
                    Save
                  </Button>
                </HStack>
              )}
              {profileImageMode === "upload" && profileImageFile && (
                <Text color={mutedColor} fontSize="sm">
                  {profileImageFile.name}
                </Text>
              )}
              {profileImageError && profileImageMode === "upload" && (
                <Text color="red.500" fontSize="sm">
                  {profileImageError}
                </Text>
              )}
              {profileImagePreviewError && (
                <Text color={mutedColor} fontSize="sm">
                  Profile image preview could not be loaded.
                </Text>
              )}
              <IconButton
                icon={<DeleteIcon />}
                size="sm"
                colorScheme="red"
                aria-label="Delete"
                onClick={handleDeleteProfileImage}
                isDisabled={!hasProfileImage || isDeletingProfileImage}
                isLoading={isDeletingProfileImage}
              />
            </Stack>
          </Flex>

          <FormControl mb={4} isDisabled={!editMode}>
            <FormLabel>Full Name</FormLabel>
            <Input name="name" value={form.name} onChange={handleChange} />
          </FormControl>

          <FormControl mb={4} isDisabled={!editMode}>
            <FormLabel>Email ID</FormLabel>
            <Input name="email" value={form.email} onChange={handleChange} />
          </FormControl>

          <FormControl mb={4} isDisabled={!editMode}>
            <FormLabel>Phone Number</FormLabel>
            <Input name="phone" value={form.phone} onChange={handleChange} />
          </FormControl>

          <FormControl mb={6} isDisabled={!editMode}>
            <FormLabel>Change Password</FormLabel>
            <Input
              type="password"
              name="password"
              placeholder="********"
              value={form.password}
              onChange={handleChange}
            />
          </FormControl>

          <FormControl mb={4} isDisabled>
            <FormLabel>Role</FormLabel>
            <Input value={form.role} isReadOnly />
          </FormControl>

          <Box borderTop="1px solid" borderColor={borderColor} pt={6} mt={6}>
            <Flex justify="space-between" align="center" mb={4}>
              <Text fontWeight="semibold">Digital Signature</Text>
              {hasSignature && (
                <Badge colorScheme="green" textTransform="capitalize">
                  {loggedInUser.digitalSignature.type}
                </Badge>
              )}
            </Flex>

            <Flex
              align="center"
              justify="center"
              bg={previewBg}
              border="1px dashed"
              borderColor={borderColor}
              borderRadius="md"
              minH="150px"
              mb={4}
              p={4}
            >
              {signaturePreview && !previewError ? (
                <Image
                  src={signaturePreview}
                  alt="Digital signature preview"
                  maxW="320px"
                  maxH="140px"
                  objectFit="contain"
                  onError={() => setPreviewError(true)}
                />
              ) : (
                <Text color={mutedColor} fontSize="sm">
                  {previewError
                    ? "Signature preview could not be loaded."
                    : "No digital signature saved."}
                </Text>
              )}
            </Flex>

            <FormControl mb={4}>
              <FormLabel>Source</FormLabel>
              <RadioGroup
                value={signatureMode}
                onChange={handleSignatureModeChange}
              >
                <HStack spacing={6}>
                  <Radio value="upload">Upload Image</Radio>
                  <Radio value="url">Image URL</Radio>
                </HStack>
              </RadioGroup>
            </FormControl>

            {signatureMode === "upload" ? (
              <FormControl mb={4} isInvalid={Boolean(signatureError)}>
                <FormLabel>Signature Image</FormLabel>
                <Button
                  as="label"
                  leftIcon={<AttachmentIcon />}
                  size="sm"
                  variant="outline"
                  cursor="pointer"
                >
                  Choose Image
                  <Input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    display="none"
                    onChange={handleSignatureFileChange}
                  />
                </Button>
                {signatureFile && (
                  <Text color={mutedColor} fontSize="sm" mt={2}>
                    {signatureFile.name}
                  </Text>
                )}
                <FormErrorMessage>{signatureError}</FormErrorMessage>
              </FormControl>
            ) : (
              <FormControl mb={4} isInvalid={Boolean(signatureError)}>
                <FormLabel>Signature URL</FormLabel>
                <Input
                  value={signatureUrl}
                  onChange={handleSignatureUrlChange}
                  placeholder="https://example.com/signature.png"
                />
                <FormErrorMessage>{signatureError}</FormErrorMessage>
              </FormControl>
            )}

            <HStack spacing={3}>
              <Button
                colorScheme="orange"
                onClick={handleSaveSignature}
                isDisabled={!canSaveSignature || isSavingSignature}
                leftIcon={isSavingSignature ? <Spinner size="xs" /> : undefined}
              >
                Save Signature
              </Button>
              <Button
                colorScheme="red"
                variant="outline"
                onClick={onOpen}
                isDisabled={!hasSignature || isDeletingSignature}
              >
                Remove Signature
              </Button>
            </HStack>
          </Box>
        </Box>
      </Box>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Remove digital signature?
            </AlertDialogHeader>

            <AlertDialogBody>
              This signature will no longer be available for future documents
              or approvals.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteSignature}
                ml={3}
                isLoading={isDeletingSignature}
              >
                Remove
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Flex>
  );
};

export default MyProfile;
